import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Heart, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import PriceDisplay from '../components/PriceDisplay';
import CouponsDialog from '../components/CouponsDialog';
import { Ticket } from 'lucide-react';

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [searchQuery, setSearchQuery] = useState('');
  const [couponsOpen, setCouponsOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const category = searchParams.get('category');

  useEffect(() => {
    fetchProducts();
  }, [category]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cms/categories`);
        const sorted = [...data].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
        setCategories(['All', ...sorted.map((c) => c.name)]);
      } catch (error) {
        // Keep the default ['All'] fallback so the filter UI remains usable
      }
    };
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/products`;
      if (category) url += `?category=${category}`;
      const { data } = await axios.get(url);
      setProducts(data);
    } catch (error) {
      toast.error('Failed to load products');
    }
  };

  const handleSearch = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/products?search=${searchQuery}`
      );
      setProducts(data);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const addToWishlist = async (e, productId) => {
    e.stopPropagation();
    if (!user) { navigate('/login'); return; }
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/wishlist/${productId}`, {}, { withCredentials: true });
      toast.success('Added to wishlist');
    } catch (error) {
      toast.error('Failed to add to wishlist');
    }
  };

  const addToCart = async (e, productId) => {
    e.stopPropagation();
    if (!user) { navigate('/login'); return; }
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/cart`, { product_id: productId, quantity: 1 }, { withCredentials: true });
      toast.success('Added to cart');
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  return (
    <div className="min-h-screen">
      <Header />

      <div className="container mx-auto px-4 py-12 pt-20">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-secondary mb-2">Our Collection</p>
          <h1 className="text-4xl font-black font-heading mb-6" data-testid="products-heading">
            {category || 'All Products'}
          </h1>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-6" data-testid="category-filters">
            {categories.map((cat) => (
              <Button
                key={cat}
                variant={(!category && cat === 'All') || category === cat ? 'default' : 'outline'}
                size="sm"
                className="rounded-full"
                onClick={() => {
                  if (cat === 'All') setSearchParams({});
                  else setSearchParams({ category: cat });
                }}
                data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {cat}
              </Button>
            ))}
          </div>

          <div className="flex gap-2 max-w-md">
            <Input
              placeholder="Search teas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              data-testid="search-input"
            />
            <Button onClick={handleSearch} data-testid="search-button">Search</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="products-grid">
          {products.map((product) => (
            <div
              key={product.product_id}
              className="group cursor-pointer"
              onClick={() => navigate(`/products/${product.product_id}`)}
              data-testid={`product-card-${product.product_id}`}
            >
              <div className="relative rounded-2xl bg-white border border-[#E0E0E0] overflow-hidden mb-3 hover:-translate-y-1 hover:shadow-lg transition-all">
                <div className="aspect-square overflow-hidden">
                  <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
                {user && (
                  <button
                    onClick={(e) => addToWishlist(e, product.product_id)}
                    className="absolute top-3 right-3 p-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white transition-colors"
                    data-testid={`wishlist-button-${product.product_id}`}
                  >
                    <Heart className="h-4 w-4 text-accent" />
                  </button>
                )}
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{product.category}</p>
              <h3 className="font-heading font-bold text-sm mb-1" data-testid={`product-name-${product.product_id}`}>{product.name}</h3>
              <div className="flex items-center justify-between">
                <PriceDisplay price={product.price} originalPrice={product.original_price} size="sm" />
                {user && (
                  <Button size="sm" className="rounded-full h-8 px-3 text-xs" onClick={(e) => addToCart(e, product.product_id)} data-testid={`add-to-cart-${product.product_id}`}>
                    <ShoppingCart className="h-3 w-3 mr-1" /> Add
                  </Button>
                )}
              </div>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setCouponsOpen(true); }}
                className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-primary hover:underline underline-offset-2 transition-colors"
                data-testid={`copy-coupon-link-${product.product_id}`}
              >
                <Ticket className="h-3 w-3" />
                Copy valid discount Coupon here
              </button>
            </div>
          ))}
        </div>

        {products.length === 0 && (
          <div className="text-center py-20">
            <p className="text-muted-foreground" data-testid="no-products-message">No teas found</p>
          </div>
        )}
      </div>

      <Footer />
      <CouponsDialog open={couponsOpen} onOpenChange={setCouponsOpen} />
    </div>
  );
};

export default ProductsPage;
